import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { QuestionOption, ResponsePayload } from '../../../types';
import { MultipleChoiceQuestion } from '../../questions/MultipleChoiceQuestion';
import { registerQuestionRenderer, type QuestionRendererComponent } from './registry';
import { isQuestionResponseValid } from 'utils/questionResponseValidation';

const getOptionText = (option: QuestionOption | undefined) => {
  if (!option) {
    return '';
  }

  if (typeof option.front_text === 'string' && option.front_text.trim().length > 0) {
    return option.front_text.trim();
  }

  if (typeof option.back_text === 'string' && option.back_text.trim().length > 0) {
    return option.back_text.trim();
  }

  if (typeof option.value === 'number') {
    return String(option.value);
  }

  return '';
};

const MultipleChoiceRenderer: QuestionRendererComponent = ({
  question,
  existingResponse,
  isQuizFlow,
  onDraftChange,
  onHasUserInputChange,
  onValidityChange,
  onExtrasChange,
}) => {
  void onExtrasChange;
  const questionId = question?.id ?? existingResponse?.question ?? -1;

  const options = useMemo(() => (Array.isArray(question?.options) ? question.options : []), [question]);
  const initialSelected = useMemo(() => {
    if (existingResponse?.selected_options && existingResponse.selected_options.length > 0) {
      const selectedId = existingResponse.selected_options[0];
      return options.some((option) => option.id === selectedId) ? selectedId : null;
    }
    return null;
  }, [existingResponse, options]);
  const initialCustomOther = existingResponse?.custom_other_value ?? '';

  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(initialSelected);
  const [customOtherValue, setCustomOtherValue] = useState<string>(initialCustomOther);
  const previousQuestionIdRef = useRef<number | null>(null);
  const selectedOption = options.find((option) => option.id === selectedOptionId);
  const activeSelectedOptionId = selectedOption?.id ?? null;

  useEffect(() => {
    const isNewQuestion = previousQuestionIdRef.current !== questionId;
    previousQuestionIdRef.current = questionId;

    if (!isNewQuestion) {
      return;
    }

    setSelectedOptionId(initialSelected);
    setCustomOtherValue(initialCustomOther);
  }, [initialSelected, initialCustomOther, questionId]);

  useEffect(() => {
    onHasUserInputChange?.(activeSelectedOptionId != null);
  }, [onHasUserInputChange, activeSelectedOptionId]);

  const draft = useMemo<ResponsePayload | null>(() => {
    if (activeSelectedOptionId == null) {
      return null;
    }

    const option = options.find((candidate) => candidate.id === activeSelectedOptionId);
    const draftPayload: ResponsePayload = {
      question: questionId,
      selected_options: [activeSelectedOptionId],
    };

    const optionText = getOptionText(option);
    if (optionText) {
      draftPayload.text_value = optionText;
    }

    if (option?.is_other) {
      const trimmedOther = customOtherValue.trim();
      if (trimmedOther.length > 0) {
        draftPayload.custom_other_value = trimmedOther;
        draftPayload.text_value = trimmedOther;
      }
    }

    return draftPayload;
  }, [customOtherValue, options, questionId, activeSelectedOptionId]);

  useEffect(() => {
    onDraftChange(draft);
    const isValid =
      Boolean(question) &&
      isQuestionResponseValid(question, {
        draft: draft ?? undefined,
        selectedOptions: draft?.selected_options,
        customOtherValue: draft?.custom_other_value,
        textValue: draft?.text_value,
      });
    onValidityChange?.(isValid);
  }, [draft, onDraftChange, onValidityChange, question]);

  const handleSelect = (optionId: number) => {
    setSelectedOptionId(optionId);

    const option = options.find((candidate) => candidate.id === optionId);
    if (!option?.is_other) {
      setCustomOtherValue('');
    }
  };

  const handleCustomOtherChange = (value: string) => {
    setCustomOtherValue(value);
  };

  return (
    <MultipleChoiceQuestion
      question={question}
      selectedOption={activeSelectedOptionId ?? undefined}
      showAnswerFeedback={!isQuizFlow}
      onChange={handleSelect}
      customOtherValue={customOtherValue}
      onCustomOtherChange={handleCustomOtherChange}
    />
  );
};

registerQuestionRenderer(['multiple_choice'], MultipleChoiceRenderer);
